/**
 * TraceActivity Component
 *
 * Individual activity item: tool call, LLM call, thinking, or message.
 * Supports expansion to show full input/output details.
 */

import { Component, Show, createSignal } from 'solid-js';
import type { ExecutionEvent } from '../../../services/api.ts';
import type { AgentInfo } from '../../../stores/executionStore.ts';
import { getAgentColorById } from '../AgentFilter.tsx';
import JsonViewer from '../JsonViewer.tsx';

export interface TraceActivityProps {
  event: ExecutionEvent;
  agents: AgentInfo[];
  formatTime: (timestamp: string) => string;
  formatDuration: (ms?: number) => string;
}

type ActivityType = 'tool' | 'llm' | 'thinking' | 'message' | 'other';

const TraceActivity: Component<TraceActivityProps> = (props) => {
  const [expanded, setExpanded] = createSignal(false);

  const payload = () => props.event.payload as Record<string, unknown>;

  const hasDetails = () => {
    const p = payload();
    return p && Object.keys(p).length > 0;
  };

  const activityType = (): ActivityType => {
    const type = props.event.eventType;
    if (type.startsWith('tool_call')) return 'tool';
    if (type.startsWith('llm_call')) return 'llm';
    if (type.startsWith('thinking')) return 'thinking';
    if (type === 'agent_message') return 'message';
    return 'other';
  };

  const isCompleted = () => props.event.eventType.includes('completed');
  const isFailed = () => props.event.eventType.includes('failed');
  const isStarted = () => props.event.eventType.includes('started');

  const getIcon = () => {
    const type = activityType();
    switch (type) {
      case 'tool':
        return (
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
      case 'llm':
        return (
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        );
      case 'thinking':
        return (
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        );
      case 'message':
        return (
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        );
      default:
        return (
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getIconColor = () => {
    if (isFailed()) return 'text-red-400';
    if (isStarted() && !isCompleted()) return 'text-cyan-400';

    const type = activityType();
    switch (type) {
      case 'tool':
        return 'text-orange-400';
      case 'llm':
        return 'text-green-400';
      case 'thinking':
        return 'text-yellow-400';
      case 'message':
        return 'text-gray-400';
      default:
        return 'text-gray-500';
    }
  };

  const getLabel = () => {
    const p = payload();
    const type = activityType();

    switch (type) {
      case 'tool':
        return p?.toolName as string || 'Tool';
      case 'llm':
        return p?.model as string || 'LLM';
      case 'thinking':
        return 'Thinking';
      case 'message':
        return 'Message';
      default:
        return props.event.eventType;
    }
  };

  const getSummary = () => {
    const p = payload();
    const type = activityType();

    switch (type) {
      case 'tool':
        if (isCompleted() && p?.result) {
          const result = typeof p.result === 'string' ? p.result : JSON.stringify(p.result);
          return result.slice(0, 100) + (result.length > 100 ? '...' : '');
        }
        if (isFailed() && p?.error) {
          return p.error as string;
        }
        return null;
      case 'thinking':
        if (p?.content) {
          const content = p.content as string;
          return content.slice(0, 150) + (content.length > 150 ? '...' : '');
        }
        return null;
      case 'message':
        return p?.message as string || null;
      case 'llm':
        if (p?.totalTokens) {
          return `${(p.totalTokens as number).toLocaleString()} tokens`;
        }
        return null;
      default:
        return null;
    }
  };

  const agentColor = () => {
    if (!props.event.agentId) return null;
    return getAgentColorById(props.event.agentId, props.agents);
  };

  const messageLevel = () => {
    if (activityType() !== 'message') return null;
    return payload()?.level as string || 'info';
  };

  return (
    <div class="group">
      <div
        class={`flex items-start gap-3 py-2 px-3 rounded-lg transition-colors ${
          hasDetails() ? 'cursor-pointer hover:bg-gray-800/50' : ''
        } ${isFailed() ? 'bg-red-500/5' : ''}`}
        onClick={() => hasDetails() && setExpanded(!expanded())}
      >
        {/* Expand indicator or bullet */}
        <div class="flex-shrink-0 w-4 flex items-center justify-center mt-0.5">
          <Show when={hasDetails()}>
            <svg
              class={`w-3 h-3 text-gray-500 transition-transform ${expanded() ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </Show>
          <Show when={!hasDetails()}>
            <span class="w-1.5 h-1.5 rounded-full bg-gray-600" />
          </Show>
        </div>

        {/* Icon */}
        <div class={`flex-shrink-0 ${getIconColor()}`}>
          {getIcon()}
        </div>

        {/* Content */}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-medium text-gray-200">{getLabel()}</span>

            {/* Agent badge */}
            <Show when={props.event.agentName && agentColor()}>
              <span class={`text-xs px-2 py-0.5 rounded-full ${agentColor()!.bg} ${agentColor()!.text} ${agentColor()!.border} border`}>
                {props.event.agentName}
              </span>
            </Show>

            {/* Status indicator for in-progress items */}
            <Show when={isStarted() && !isCompleted() && !isFailed()}>
              <span class="flex items-center gap-1">
                <span class="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span class="text-xs text-cyan-400">Running</span>
              </span>
            </Show>

            {/* Model badge for LLM */}
            <Show when={activityType() === 'llm' && payload()?.model}>
              <span class="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 border border-green-500/40">
                {payload()?.model as string}
              </span>
            </Show>

            {/* Error badge */}
            <Show when={isFailed()}>
              <span class="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40">
                Failed
              </span>
            </Show>
          </div>

          {/* Summary line */}
          <Show when={getSummary()}>
            <p class={`text-sm line-clamp-2 mt-0.5 ${
              isFailed() ? 'text-red-400' :
              messageLevel() === 'error' ? 'text-red-400' :
              messageLevel() === 'warning' ? 'text-yellow-400' :
              activityType() === 'thinking' ? 'text-gray-400 italic' :
              'text-gray-400'
            }`}>
              {getSummary()}
            </p>
          </Show>
        </div>

        {/* Timing */}
        <div class="flex-shrink-0 text-right">
          <Show when={props.event.durationMs}>
            <div class="text-xs text-gray-500">{props.formatDuration(props.event.durationMs)}</div>
          </Show>
        </div>
      </div>

      {/* Expanded details */}
      <Show when={expanded() && hasDetails()}>
        <div class="ml-11 mr-4 mb-2 p-3 rounded-lg bg-gray-900/50 border border-gray-700 animate-fade-in">
          <JsonViewer data={props.event.payload} expanded={true} />
        </div>
      </Show>
    </div>
  );
};

export default TraceActivity;
